import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { proposalTemplateEndpoints } from '@/lib/api/endpoints';
import { proposalTemplateKeys } from '@/lib/queryKeys';
import { useApiQuery } from '../useApiQuery';
import type { Attachment } from '@/types/attachment';

export function useProposalTemplateAttachments(templateId: string) {
  return useApiQuery<Attachment[]>({
    queryKey: proposalTemplateKeys.attachments(templateId),
    queryFn: () =>
      apiGet<Attachment[]>(operationsClient, proposalTemplateEndpoints.attachments(templateId)),
    enabled: Boolean(templateId),
  });
}

export function useDeleteProposalTemplateAttachment(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      apiDelete(
        operationsClient,
        proposalTemplateEndpoints.deleteAttachment(templateId, attachmentId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: proposalTemplateKeys.attachments(templateId),
      });
    },
    onError: () => {
      toast.error('Failed to delete attachment');
    },
  });
}

export function useUploadProposalTemplateAttachment(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation<Attachment, Error, { file: File; isInternal?: boolean }>({
    mutationFn: async ({ file, isInternal = false }) => {
      const res = await apiPost<{ uploadUrl: string; attachment: Attachment }>(
        operationsClient,
        proposalTemplateEndpoints.attachmentUploadUrl(templateId),
        {
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          isInternal,
        },
      );

      const s3Res = await fetch(res.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!s3Res.ok) {
        throw new Error(`Upload failed: ${s3Res.status} ${s3Res.statusText}`);
      }

      return res.attachment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: proposalTemplateKeys.attachments(templateId),
      });
    },
    onError: () => {
      toast.error('Failed to upload attachment');
    },
  });
}
